import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Typography,
} from "@mui/material";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/mode-lua";
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/webpack-resolver";
import { useAtom } from "jotai/index";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { AAutocompleteElement } from "@/components/a-autocomplete";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { isOwner } from "@/lib/auth-predicates";
import {
  AiConfig,
  aiConfigDefaultValues,
} from "@/module/service-configs-module/ai-configs-module/type";
import { SysCfgCreateRequestDto } from "@/shared/api";
import { meStore } from "@/store/store";

export default function AiConfigModal({
  open,
  mode,
  onSave,
  onCancel,
  initialData = aiConfigDefaultValues,
}: {
  open: boolean;
  mode?: string;
  initialData?: AiConfig;
  onSave?: (data: SysCfgCreateRequestDto) => void;
  onCancel?: () => void;
  confirmLoading?: boolean;
}) {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const aiConfigSchema = z.object({
    name: z.string().min(1, t("fieldRequired")),
    auth_groups: isOwner(me)
      ? z.array(z.string())
      : z.array(z.string()).min(1, t("mustContainsGroup")),
    enabled: z.boolean().default(true),
    data: z.object({
      key: z.string().min(1, t("fieldRequired")),
      type: z.string().min(1, t("fieldRequired")),
    }),
  });

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs?.data]);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<AiConfig>({
    mode: "onTouched",
    resolver: zodResolver(aiConfigSchema),
    defaultValues: { ...initialData },
  });

  useEffect(() => {
    reset();
  }, [open, reset]);

  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const MAX_FILE_SIZE = 50 * 1024; // 50 KB
  const handleFileUpload = (file: File) => {
    const isP12 = file.type === "application/x-pkcs12" || file.name.endsWith(".p12");

    if (!isP12) {
      enqueueSnackbar(t("uploadOnlyP12File") || "Допустимы только .p12 файлы", {
        variant: "error",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(t("p12MustBeSmaller") || "Файл должен быть не более 50 КБ", {
        variant: "error",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), "");
      const base64Str = btoa(binary);
      setValue("data.key", base64Str, { shouldValidate: true });
      clearErrors("data.key");
      setSelectedFileName(file.name);
    };
    reader.readAsArrayBuffer(file);
  };

  const onSubmit: SubmitHandler<AiConfig> = (data) => {
    const result = {
      ...data,
      auth_groups: new Set(data.auth_groups),
    };

    if (!onSave) return;
    onSave(result);

    onCancel && onCancel(); //purifying form and close dialog
  };

  return (
    <>
      <Dialog
        open={open}
        key="ai-config-modal"
        onClose={(event, reason) => {
          if (
            reason === "backdropClick" &&
            isDirty &&
            !window.confirm("Data has been changed. Do you want discard changes?")
          )
            return; //ask question if changes==true and click outSide
          onCancel?.();
        }}
      >
        <DialogTitle>
          {t("addNewAiConfig")}
          <IconButton
            aria-label="close"
            onClick={() => onCancel && onCancel()}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px" /* Расстояние между элементами */,
              width: "500px" /* Ширина формы */,
            }}
          >
            <AAutocompleteElement
              control={control}
              loading={authGroupsIsLoading}
              name="auth_groups"
              multiple
              options={authGroupsOptions ?? []}
              label={t("authGroups")}
              textFieldProps={{
                error: !!errors.auth_groups,
                helperText: errors.auth_groups?.message ?? " ",
                variant: "standard",
              }}
            />

            <TextFieldElement
              name="name"
              control={control}
              label={t("name")}
              placeholder={t("name")}
              fullWidth
              error={!!errors.name?.message}
              helperText={errors.name?.message}
              slotProps={{
                input: {
                  disabled: mode === "edit",
                },
              }}
            />

            <Box display="flex" flexDirection="column" gap={2}>
              <InputLabel>Key: {selectedFileName ?? "upload .p12 file"}</InputLabel>

              <Button variant="contained" component="label">
                Choose file
                <input
                  type="file"
                  hidden
                  accept=".p12"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                  }}
                />
              </Button>

              {errors?.data?.key && (
                <Typography color="error">{errors?.data.key.message}</Typography>
              )}
            </Box>

            <TextFieldElement
              name="data.type"
              control={control}
              label={t("type")}
              placeholder={t("type")}
              fullWidth
              error={!!errors.name?.message}
              helperText={errors.name?.message}
            />

            <ModalActionsButtons
              onCancelClick={() => onCancel && onCancel()}
              onConfirmClick={() => void 0}
              //loading={confirmLoading}
            />
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
